import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  DOSA_FLAVORS, dosaDisplayName, addToDraft, aggregateDosas, regularGriddleOrders,
  itemRemaining, isOrderDone, orderProgress, summarizeDay,
} from '../buffetUtils';
import {
  createBuffetOrder, bumpDosaMade, completeBuffetOrder, reopenBuffetOrder, removeBuffetOrder,
} from '../buffetActions';
import { useBuffetOrders } from '../hooks/useBuffetOrders';
import { useLiveOrders } from '../hooks/useLiveOrders';
import { useTicketChecks } from '../hooks/useTicketChecks';
import { isStoreOpen } from '../ticketUtils';
import { Stepper } from '../components/Stepper';
import { useLang, useAutoTranslate } from '../i18n';
import '../BuffetTab.css';

const lsGet = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };
const waitMins = (ms, now) => (ms ? Math.max(0, Math.round((now - ms) / 60000)) : 0);

// ── Density and legibility are two separate controls ────────────────────────
// A single "zoom" used to mean both at once, so the only way to fit another
// order on screen was to shrink the text — and on a griddle iPad the text is
// the whole point. Columns now sets how many order cards share a row; text size
// only sets how big the words are. Same split as the Live Orders board.
//
// The text ladder stays coarse as it climbs so the griddle iPad reaches 3× in a
// few taps instead of fourteen. Values are the card `zoom` factor.
const TEXT_STEPS = [0.6, 0.7, 0.8, 0.9, 1, 1.15, 1.3, 1.5, 1.75, 2, 2.5, 3];
const TEXT_DEFAULT_IDX = TEXT_STEPS.indexOf(1);
const COLS_MIN = 1, COLS_MAX = 8;
const nearestTextIdx = (z) => TEXT_STEPS.reduce((best, v, i) => (
  Math.abs(v - z) < Math.abs(TEXT_STEPS[best] - z) ? i : best
), 0);

/** Explicit column count, or null to follow the breakpoint (Auto). */
function readBfCols() {
  const n = parseInt(lsGet('bf_cols', ''), 10);
  return Number.isFinite(n) && n >= COLS_MIN && n <= COLS_MAX ? n : null;
}

/** Text step, carrying a pre-split `bf_zoom` over to the nearest size. */
function readBfText() {
  const saved = parseInt(lsGet('bf_text', ''), 10);
  if (Number.isFinite(saved) && saved >= 0 && saved < TEXT_STEPS.length) return saved;
  const legacy = parseFloat(lsGet('bf_zoom', ''));
  return Number.isFinite(legacy) ? nearestTextIdx(legacy) : TEXT_DEFAULT_IDX;
}

export function BuffetTab() {
  const { t } = useLang();
  const { orders, loading, error } = useBuffetOrders();
  const [view, setView] = useState(() => lsGet('bf_view', 'new'));
  const [now, setNow] = useState(Date.now());
  const [maximized, setMaximized] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(id); }, []);
  useEffect(() => lsSet('bf_view', view), [view]);

  // Columns + text size — same model as the Live Orders (KDS) board: persisted
  // per device, card-level CSS zoom for the text, explicit track count for the
  // density. Auto still widens the track as the text grows, because `zoom`
  // shrinks a card's internal coordinate space — a fixed track at 3× would
  // squeeze the words into more lines instead of making them bigger.
  const [cols, setCols] = useState(readBfCols);
  const [textIdx, setTextIdx] = useState(readBfText);
  const textScale = TEXT_STEPS[textIdx];
  const autoColsRef = useRef(null);
  const [belowCount, setBelowCount] = useState(0);

  const stepText = (dir) => setTextIdx(i => {
    const n = Math.max(0, Math.min(TEXT_STEPS.length - 1, i + dir));
    lsSet('bf_text', String(n)); return n;
  });
  const resetText = () => { setTextIdx(TEXT_DEFAULT_IDX); lsSet('bf_text', String(TEXT_DEFAULT_IDX)); };
  const stepCols = (dir) => setCols(prev => {
    // Stepping off Auto starts from the count already on screen, so the first
    // press always visibly changes something.
    const from = prev ?? autoColsRef.current ?? 2;
    const n = Math.min(COLS_MAX, Math.max(COLS_MIN, from + dir));
    lsSet('bf_cols', String(n)); return n;
  });
  const resetCols = () => {
    setCols(null);
    try { localStorage.removeItem('bf_cols'); } catch {}
  };

  // Sun mode — a high-contrast light palette for the griddle iPad. Dark UIs wash
  // out in daylight because reflected glare lifts the dark background far more
  // than the light text; black-on-white keeps its separation outdoors.
  const [sun, setSun] = useState(() => lsGet('bf_sun', '0') === '1');
  const toggleSun = () => setSun(s => { lsSet('bf_sun', s ? '0' : '1'); return !s; });

  // Keep the maximize state in sync if the user leaves fullscreen via Esc.
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setMaximized(false); };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  function toggleMax() {
    if (!maximized) { setMaximized(true); rootRef.current?.requestFullscreen?.().catch(() => {}); }
    else { setMaximized(false); if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); }
  }

  const openOrders = useMemo(() => orders.filter(o => !isOrderDone(o)), [orders]);   // oldest-first
  const doneOrders = useMemo(() => orders.filter(o => isOrderDone(o)).reverse(), [orders]);
  const day = useMemo(() => summarizeDay(orders), [orders]);

  // Regular (Square) live orders — shown on top of the Orders tab so the chef
  // sees both at once. Only polled while actually viewing Orders.
  const { orders: liveOrders } = useLiveOrders(view === 'active' && isStoreOpen() ? 10000 : 0);
  const liveIds = useMemo(() => liveOrders.map(o => o.id).filter(Boolean), [liveOrders]);
  const checks = useTicketChecks(liveIds);
  const activeRegular = useMemo(
    () => regularGriddleOrders(liveOrders.filter(o => !(checks[o.id]?.manuallyCompleted)), checks),
    [liveOrders, checks]
  );

  // ── Off-screen queue depth ────────────────────────────────────────────────
  // The board scrolls, so an order card below the fold is invisible — and an
  // invisible order is a dosa nobody is making. Count what's below and offer one
  // tap to page down to it. IntersectionObserver keeps this off the scroll path.
  const cardsKey = [
    view,
    openOrders.map(o => o.id).join(','),
    doneOrders.map(o => o.id).join(','),
    activeRegular.map(o => o.id).join(','),
  ].join('|');
  /** Maximized pins .bf to the viewport with its own scrollbar; otherwise the
   *  page scrolls. */
  const getScroller = useCallback(
    () => (maximized ? rootRef.current : null), [maximized]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') { setBelowCount(0); return; }
    const cards = root.querySelectorAll('.bf__ocard');
    if (!cards.length) { setBelowCount(0); return; }
    const below = new Set();
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const foldY = e.rootBounds ? e.rootBounds.bottom : window.innerHeight;
        if (!e.isIntersecting && e.boundingClientRect.top >= foldY) below.add(e.target);
        else below.delete(e.target);
      });
      setBelowCount(below.size);
    }, { root: getScroller(), threshold: 0 });
    cards.forEach(c => io.observe(c));
    return () => io.disconnect();
    // Re-observe when the cards change, or when a layout control moves them
    // across the fold.
  }, [cardsKey, getScroller, cols, textScale, loading]);

  const pageDown = useCallback(() => {
    const scroller = getScroller();
    const target = scroller || window;
    const viewportH = (scroller ? scroller.clientHeight : window.innerHeight) || 600;
    target.scrollBy({ top: Math.round(viewportH * 0.82), behavior: 'smooth' });
  }, [getScroller]);

  // What Auto currently resolves to, so stepping off Auto starts from the count
  // already on screen instead of jumping.
  useEffect(() => {
    const grid = rootRef.current?.querySelector('.bf__orders');
    if (!grid || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const tpl = getComputedStyle(grid).gridTemplateColumns;
      autoColsRef.current = tpl && tpl !== 'none' ? tpl.split(' ').filter(Boolean).length : null;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(grid);
    return () => ro.disconnect();
  }, [cardsKey, cols, textScale]);

  return (
    <div ref={rootRef} className={`bf${maximized ? ' bf--max' : ''}${sun ? ' bf--sun' : ''}`}
      style={{
        '--bf-text': textScale,
        // Inherited by every .bf__orders grid on the board — including the one
        // inside CompletedList — so an explicit column count needs no prop
        // drilling. Left unset, each grid falls back to its Auto track.
        ...(cols ? { '--bf-grid': `repeat(${cols}, minmax(0, 1fr))` } : {}),
      }}>
      <div className="bf__topbar">
        <div className="bf__modes">
          <button className={`bf__modebtn${view === 'new' ? ' on' : ''}`} onClick={() => setView('new')}>➕ {t('bf.viewNew')}</button>
          <button className={`bf__modebtn${view === 'active' ? ' on' : ''}`} onClick={() => setView('active')}>🍳 {t('bf.viewOrders')} <span className="bf__pill">{openOrders.length}</span></button>
          <button className={`bf__modebtn${view === 'done' ? ' on' : ''}`} onClick={() => setView('done')}>✅ {t('bf.viewDone')} <span className="bf__pill">{doneOrders.length}</span></button>
        </div>
        <div className="bf__daystat">
          {/* The day's numbers are read from across the kitchen, so they follow
              the Text control too (capped, so the toolbar never outgrows the
              board). "To make" is the one the griddle acts on — it leads. */}
          <div className="bf__stats">
            <span className="bf__stat bf__stat--key"><b>{day.remaining}</b> {t('bf.toMake')}</span>
            <span className="bf__stat"><b>{day.totalMade}</b> {t('bf.made')}</span>
            <span className="bf__stat"><b>{day.openOrders}</b> {t('bf.open')}</span>
          </div>
          <Stepper variant="bf" ico="▦" label={t('bf.cols')} value={cols ?? t('bf.colsAuto')}
            onLess={() => stepCols(-1)} onMore={() => stepCols(1)} onReset={resetCols}
            lessTitle={t('bf.colsFewer')} moreTitle={t('bf.colsMore')} resetTitle={t('bf.colsReset')}
            lessDisabled={cols === COLS_MIN} moreDisabled={cols === COLS_MAX} />
          <Stepper variant="bf" ico="A" label={t('bf.text')} value={`${Math.round(textScale * 100)}%`}
            onLess={() => stepText(-1)} onMore={() => stepText(1)} onReset={resetText}
            lessTitle={t('bf.textSmaller')} moreTitle={t('bf.textBigger')} resetTitle={t('bf.textReset')}
            lessDisabled={textIdx === 0} moreDisabled={textIdx === TEXT_STEPS.length - 1} />
          <button className="bf__maxbtn bf__sunbtn" onClick={toggleSun}
            title={sun ? t('bf.sunOff') : t('bf.sunOn')}>
            {sun ? `🌙 ${t('bf.dark')}` : `☀️ ${t('bf.sun')}`}
          </button>
          <button className="bf__maxbtn" onClick={toggleMax} title={maximized ? t('bf.minimize') : t('bf.maximize')}>
            {maximized ? `🗗 ${t('bf.minimize')}` : `⛶ ${t('bf.maximize')}`}
          </button>
        </div>
      </div>

      {error && <div className="td__error-banner">⚠️ {error}</div>}

      {view === 'new' && (
        <div className="bf__split">
          <EntryPanel />
          <div className="bf__right">
            <h3 className="bf__rtitle">{t('bf.activeOrders')} {openOrders.length > 0 && <span className="bf__count">{openOrders.length}</span>}</h3>
            {loading && <p className="bf__empty">{t('bf.loading')}</p>}
            {!loading && openOrders.length === 0 && <p className="bf__empty bf__empty--big">{t('bf.emptyActive')}</p>}
            <div className="bf__orders">
              {openOrders.map(o => <OrderCard key={o.id} order={o} now={now} />)}
            </div>
          </div>
        </div>
      )}

      {view === 'active' && (
        <>
          {activeRegular.length > 0 && (
            <div className="bf__regular">
              <h3 className="bf__rtitle">🍽️ {t('bf.regularOrders')} <span className="bf__count">{activeRegular.length}</span> <span className="bf__rsub">{t('bf.regularSub')}</span></h3>
              <div className="bf__orders">
                {activeRegular.map(o => <RegularCard key={o.id} order={o} />)}
              </div>
            </div>
          )}

          <h3 className="bf__rtitle">🥞 {t('bf.buffetDosas')} {openOrders.length > 0 && <span className="bf__count">{openOrders.length}</span>}</h3>
          {loading && <p className="bf__empty">{t('bf.loading')}</p>}
          {!loading && openOrders.length === 0 && <p className="bf__empty bf__empty--big">{t('bf.emptyBuffet')}</p>}
          <div className="bf__orders">
            {openOrders.map(o => <OrderCard key={o.id} order={o} now={now} />)}
          </div>
        </>
      )}

      {view === 'done' && <CompletedList orders={doneOrders} />}

      {/* Queue views only. In New Order the cards are a secondary glance list
          that already carries its own "Active Orders {n}" heading, and on a
          stacked (narrow) layout a bottom-centred chip would sit right on top
          of the Save button while the counter is taking an order. */}
      {belowCount > 0 && view !== 'new' && (
        <button className="kds__more-chip" onClick={pageDown} title={t('bf.moreBelowTitle')}>
          <span className="kds__more-chip-ico" aria-hidden="true">▼</span>
          {t('bf.moreBelow', { n: belowCount })}
        </button>
      )}
    </div>
  );
}

// ── Voice input (Web Speech API) ─────────────────────────────────────────────

function MicButton({ onText, lang = 'en-IN' }) {
  const { t } = useLang();
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  useEffect(() => () => { try { recRef.current?.abort?.(); } catch {} }, []);

  const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SR) return null;

  function toggle() {
    if (listening) { try { recRef.current?.stop(); } catch {} return; }
    const rec = new SR();
    rec.lang = lang; rec.interimResults = true; rec.continuous = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      let t = '';
      for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      onText(t.trim());
    };
    rec.onend = () => { setListening(false); recRef.current = null; };
    rec.onerror = () => { setListening(false); recRef.current = null; };
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch { setListening(false); }
  }

  return (
    <button type="button" className={`bf__mic${listening ? ' on' : ''}`} onClick={toggle}
      title={listening ? t('bf.micListening') : t('bf.micIdle')}>
      {listening ? '🎙️' : '🎤'}
    </button>
  );
}

// ── New Order (counter) ──────────────────────────────────────────────────────

function EntryPanel({ onSaved }) {
  const [name, setName] = useState('');
  const [flavor, setFlavor] = useState('plain');
  const [ghee, setGhee] = useState(false);
  const [onion, setOnion] = useState(false);
  const [draft, setDraft] = useState([]);
  const [sending, setSending] = useState(false);
  const [savedFlash, setSavedFlash] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  const [customText, setCustomText] = useState('');
  const nameRef = useRef(null);
  const { t, ln } = useLang();

  const spec = { flavor, ghee, onion };
  const preview = dosaDisplayName(spec);
  // Register every dosa name/flavor visible here so Telugu mode resolves them
  // (curated Sunday-buffet terms win; custom names fall through to auto-translate).
  useAutoTranslate([preview, ...DOSA_FLAVORS.map(f => f.label), ...draft.map(d => d.name)]);
  const draftTotal = draft.reduce((s, d) => s + d.qty, 0);
  const hasName = name.trim().length > 0;
  const canSave = hasName && draft.length > 0;
  const saveLabel = sending ? t('bf.saving')
    : !hasName ? t('bf.needName')
    : draft.length === 0 ? t('bf.needDosa')
    : t('bf.saveOrder', { n: draftTotal });

  function add() { setDraft(d => addToDraft(d, spec, 1)); }
  function addCustom() {
    const nm = customText.trim();
    if (!nm) return;
    setDraft(d => addToDraft(d, { flavor: 'custom', name: nm }, 1));
    setCustomText('');
  }
  function removeLine(i) { setDraft(d => d.filter((_, idx) => idx !== i)); }
  function changeQty(i, delta) { setDraft(d => d.map((it, idx) => idx === i ? { ...it, qty: Math.max(1, it.qty + delta) } : it)); }

  async function send() {
    if (sending || !canSave) return;
    setSending(true);
    const savedName = name.trim();
    try {
      await createBuffetOrder(name, draft);
      setDraft([]); setName(''); setFlavor('plain'); setGhee(false); setOnion(false);
      setSavedFlash(savedName);
      setTimeout(() => setSavedFlash(''), 2200);
      nameRef.current?.focus();
      if (onSaved) onSaved();
    } catch (e) { alert(t('bf.saveFailed', { msg: e.message })); }
    finally { setSending(false); }
  }

  return (
    <div className="bf__left">
      {/* Step 1 — build the dosa. Flavour and add-ons compose the name shown on
          the Add button, so the button doubles as the live preview. */}
      <div className="bf__pickcol">
        <div className="bf__field">
          <span className="bf__flabel">{t('bf.flavor')}</span>
          <div className="bf__chips">
            {DOSA_FLAVORS.map(f => (
              <button key={f.key} className={`bf__chip${flavor === f.key ? ' on' : ''}`} onClick={() => setFlavor(f.key)}>{ln(f.label)}</button>
            ))}
          </div>
        </div>

        <div className="bf__field">
          <span className="bf__flabel">{t('bf.addons')}</span>
          <div className="bf__chips">
            <button className={`bf__chip bf__chip--tog${ghee ? ' on' : ''}`} onClick={() => setGhee(g => !g)}>🧈 {ln('Ghee')}</button>
            <button className={`bf__chip bf__chip--tog${onion ? ' on' : ''}`} onClick={() => setOnion(o => !o)}>🧅 {ln('Onion')}</button>
            <button type="button" className={`bf__chip bf__chip--tog${customOpen ? ' on' : ''}`} onClick={() => setCustomOpen(o => !o)}>✏️ {t('bf.custom')}</button>
          </div>
          {customOpen && (
            <div className="bf__customrow">
              <input className="bf__name" placeholder={t('bf.customPlaceholder')} value={customText}
                onChange={e => setCustomText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCustom(); }} />
              <button className="bf__add bf__add--sm" onClick={addCustom} disabled={!customText.trim()}>+ {t('bf.add')}</button>
            </div>
          )}
        </div>

        <button className="bf__add" onClick={add} title={t('bf.addTitle', { name: ln(preview) })}>
          <span className="bf__addplus" aria-hidden="true">+</span>
          <span className="bf__addname">{ln(preview)}</span>
        </button>
      </div>

      {/* Step 2 — who it's for, what's on it, save. The name sits directly under
          the Add button (and above the line list) so it keeps one fixed spot on
          screen instead of sliding down as dosas are added. */}
      <div className="bf__draft">
        {savedFlash && <div className="bf__savedflash">{t('bf.savedFor', { name: savedFlash })}</div>}

        <div className="bf__field">
          <span className="bf__flabel">{t('bf.customerName')} <span className="bf__req">{t('bf.required')}</span></span>
          <div className="bf__namewrap">
            {/* Once there's a dosa on the ticket, an empty name is the only thing
                left between the counter and Save — so the field says so itself
                rather than leaving the greyed-out Save button to explain. */}
            {/* No autoFocus: it made sense when this was the first field, but
                now it would pop the on-screen keyboard over the flavour chips
                the counter is meant to reach for first. Focus still returns
                here after a save, ready for the next ticket. */}
            <input ref={nameRef}
              className={`bf__name${draft.length > 0 && !hasName ? ' bf__name--need' : ''}`}
              placeholder={t('bf.namePlaceholder')}
              value={name} onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && canSave) send(); }} />
            <MicButton onText={setName} />
            {name && <button type="button" className="bf__nameclear" onClick={() => setName('')} title={t('bf.clear')}>✕</button>}
          </div>
        </div>

        <div className="bf__field">
          <h3>{t('bf.thisOrder')} {draftTotal > 0 && <span className="bf__count">{t(draftTotal === 1 ? 'bf.dosaN' : 'bf.dosasN', { n: draftTotal })}</span>}</h3>
          {draft.length === 0
            ? <p className="bf__empty">{t('bf.pickHint')}</p>
            : (
              <ul className="bf__draftlist">
                {draft.map((it, i) => (
                  <li key={i}>
                    <span className="bf__dname">{ln(it.name)}</span>
                    {/* Wet or gloved hands on a counter iPad — the quantity keys
                        are sized to be hit without looking. − stops at 1; taking
                        the last one off the ticket is the ✕, which is a
                        deliberately separate target. */}
                    <div className="bf__stepper">
                      <button type="button" onClick={() => changeQty(i, -1)} disabled={it.qty <= 1}
                        aria-label={t('bf.qtyLess', { name: ln(it.name) })}>−</button>
                      <span aria-live="polite">{it.qty}</span>
                      <button type="button" onClick={() => changeQty(i, 1)}
                        aria-label={t('bf.qtyMore', { name: ln(it.name) })}>+</button>
                    </div>
                    <button className="bf__rm" onClick={() => removeLine(i)} title={t('bf.remove')}
                      aria-label={t('bf.remove')}>✕</button>
                  </li>
                ))}
              </ul>
            )}
        </div>

        <button className="bf__send" onClick={send} disabled={sending || !canSave}>
          {saveLabel}
        </button>
      </div>
    </div>
  );
}

// ── Orders (chef) ────────────────────────────────────────────────────────────

function OrderCard({ order, now }) {
  const [busy, setBusy] = useState(null);
  const busyRef = useRef(false);
  const p = orderProgress(order);
  const { t, ln } = useLang();
  useAutoTranslate(order.items.map(it => it.name));

  async function guard(key, fn) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(key);
    try { await fn(); } catch (e) { alert(t('bf.updateFailed', { msg: e.message })); }
    finally { busyRef.current = false; setBusy(null); }
  }

  return (
    <div className="bf__ocard">
      <div className="bf__ohead">
        {order.nameImage
          ? <img className="bf__nameimg" src={order.nameImage} alt={order.customerName || 'name'} />
          : <span className="bf__oname">{order.customerName || t('bf.guest')}</span>}
        <span className="bf__owait">{waitMins(order.createdAtMs, now)}m</span>
        {/* Rollup only earns its space on multi-line orders — a single line
            already shows the same number, big, right below. */}
        {order.items.length > 1 && <span className="bf__oprog">{t('bf.leftN', { n: p.remaining })}</span>}
      </div>
      <ul className="bf__olines">
        {order.items.map(it => {
          const rem = itemRemaining(it);
          const k = order.id + '_' + it.id;
          const done = rem === 0;
          return (
            <li key={it.id}
              className={`bf__oline${done ? ' done' : ''}`}
              onClick={() => !done && guard(k, () => bumpDosaMade(order.id, it.id, rem))}
              title={done ? undefined : t('bf.tapCooked')}
            >
              <span className={`bf__qty${done ? ' bf__qty--done' : ''}`}>{done ? '✓' : rem}</span>
              <span className={`bf__dname${done ? ' bf__dname--done' : ''}`}>{ln(it.name)}</span>
              {!done && it.made > 0 && <span className="bf__madeqty">{t('bf.madeN', { n: it.made })}</span>}
            </li>
          );
        })}
      </ul>
      <div className="bf__oactions">
        <button className="bf__done" disabled={busy === 'done'} onClick={() => guard('done', () => completeBuffetOrder(order.id))}>
          ✓ {t('bf.done')}
        </button>
        <button className="bf__rm bf__rm--order" onClick={() => guard('rm', () => removeBuffetOrder(order.id))} title={t('bf.removeOrder')}>✕</button>
      </div>
    </div>
  );
}

// ── Regular (Square) order — read-only, shown atop the Orders tab ────────────

function RegularCard({ order }) {
  const label = order.ticketName || order.customerName || ('#' + (order.shortId || ''));
  const items = order.items || [];
  const { t, ln } = useLang();
  useAutoTranslate([...items.map(it => it.name), ...items.map(it => (it.note || '').trim())].filter(Boolean));
  return (
    <div className="bf__ocard bf__ocard--reg">
      <div className="bf__ohead">
        <span className="bf__oname">{label}</span>
        <span className="bf__regtag">{t('bf.regularTag')}</span>
      </div>
      <ul className="bf__olines">
        {items.length === 0 && <li className="bf__empty">{t('bf.noItems')}</li>}
        {items.map((it, i) => (
          <li key={i} className={`bf__oline bf__regitem${it.done ? ' done' : ''}`}>
            <div className="bf__regrow">
              <span className={`bf__qty${it.done ? ' bf__qty--done' : ''}`}>{it.done ? '✓' : (it.qty || 1)}</span>
              <span className={`bf__dname${it.done ? ' bf__dname--done' : ''}`}>{ln(it.name)}</span>
            </div>
            {it.note && <div className="bf__regnote"><span className="bf__regnote-lbl">{t('bf.note')}</span> {ln(it.note)}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Completed ────────────────────────────────────────────────────────────────

function CompletedList({ orders }) {
  const { t, ln } = useLang();
  const agg = aggregateDosas(orders);                       // done → ordered === made
  // Hooks must run before the early return below, so register names up front.
  useAutoTranslate([...agg.map(d => d.name), ...orders.flatMap(o => (o.items || []).map(it => it.name))]);
  if (orders.length === 0) return <p className="bf__empty bf__empty--big">{t('bf.emptyDone')}</p>;
  const totalDosas = agg.reduce((s, d) => s + d.ordered, 0);
  const topName = agg[0]?.name || '—';

  return (
    <div className="bf__completed">
      <div className="bf__analysis">
        <div className="bf__astat"><span className="bf__anum">{orders.length}</span><span className="bf__albl">{t('bf.ordersDone')}</span></div>
        <div className="bf__astat"><span className="bf__anum">{totalDosas}</span><span className="bf__albl">{t('bf.dosasMade')}</span></div>
        <div className="bf__astat"><span className="bf__anum">{agg.length}</span><span className="bf__albl">{t('bf.dosaTypes')}</span></div>
        <div className="bf__astat"><span className="bf__anum bf__anum--sm">{ln(topName)}</span><span className="bf__albl">{t('bf.mostPopular')}</span></div>
      </div>

      <div className="bf__breakdown">
        <span className="bf__flabel">{t('bf.breakdown')}</span>
        <div className="bf__abreak">
          {agg.map(d => <span key={d.key} className="bf__achip">{ln(d.name)} <b>×{d.ordered}</b></span>)}
        </div>
      </div>

      <div className="bf__orders">
      {orders.map(o => (
        <div key={o.id} className="bf__ocard bf__ocard--done">
          <div className="bf__ohead">
            {o.nameImage
              ? <img className="bf__nameimg" src={o.nameImage} alt={o.customerName || 'name'} />
              : <span className="bf__oname">{o.customerName || t('bf.guest')}</span>}
            <span className="bf__okcheck">✅</span>
          </div>
          <div className="bf__doneitems">{(o.items || []).map(it => `${ln(it.name)}${it.qty > 1 ? ` ×${it.qty}` : ''}`).join(', ')}</div>
          <button className="bf__reopen" onClick={() => reopenBuffetOrder(o.id)}>↩ {t('bf.reopen')}</button>
        </div>
      ))}
      </div>
    </div>
  );
}
